import { useState } from 'react';
import type { ReactNode } from 'react';
import { CN_DOWNLINK_NOT_READY_PREFIX, SAMPLING_SCOPES, samplingPolicyKey } from '@nadoutong/contracts';
import type {
  LegacyLifecycleSamplingValues,
  ListSamplingPoliciesResponse,
  SamplingPoliciesWriteGate,
  SamplingPolicyInput,
} from '@nadoutong/contracts';
import { OpsApiError } from '../../api/client.ts';
import { usePutSamplingPolicies, useSamplingPolicies } from '../../api/useSamplingPolicies.ts';
import { ROLE_OPS_ADMIN } from '../../auth/opsRoles.ts';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { CustomerCell } from '../../components/identity/CustomerCell.tsx';
import { useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { canAccessRoles } from '../../layout/navigation.ts';
import { Button, Chip, ConfirmDialog, Table, TBody, THead, Td, Th, Tr } from '../../ui/index.ts';
import type { ChipTone } from '../../ui/index.ts';
import { SCOPE_LABEL, SamplingPolicyDialog } from './SamplingPolicyDialog.tsx';
import {
  SAMPLING_POLICY_GROUPS,
  draftFromInput,
  emptyDraft,
  policiesInGroup,
  toPolicyInput,
} from './samplingPolicyFields.ts';
import type { SamplingPolicyDraft, SamplingPolicyGroup } from './samplingPolicyFields.ts';

type SourceKind = ListSamplingPoliciesResponse['sources'][keyof ListSamplingPoliciesResponse['sources']];

const SOURCE_TONE: Record<SourceKind, ChipTone> = {
  table: 'signal',
  legacy: 'warn',
  keep_all: 'neutral',
};

const SOURCE_LABEL: Record<SourceKind, I18nKey> = {
  table: 'tenant_routing.load.sampling.source.table',
  legacy: 'tenant_routing.load.sampling.source.legacy',
  keep_all: 'tenant_routing.load.sampling.source.keep_all',
};

const STREAM_LABEL: Record<SamplingPolicyGroup['stream'], I18nKey> = {
  trace: 'tenant_routing.load.sampling.stream.trace',
  lifecycle: 'tenant_routing.load.sampling.stream.lifecycle',
};

interface PolicyDraftSet {
  readonly rows: readonly SamplingPolicyInput[];
  /** `version` of the GET the draft was started from; echoed as `expected_version`. */
  readonly baseVersion: string;
}

interface EditTarget {
  /** Key of the row being edited; null = a new row. */
  readonly originalKey: string | null;
  readonly initial: SamplingPolicyDraft;
}

/**
 * Standard sampling policies (#2862): one table for trace bodies and
 * lifecycle timelines, per region and scope. Edits stay in a local draft;
 * saving PUTs the whole set with the GET's version, so a concurrent edit is
 * a 409 instead of a silent overwrite. Saving is two steps: a dry run first;
 * if it returns warnings the operator confirms them before the real PUT.
 * While the CN downlink is not ready for the table (`write_gate`, or a 403
 * `cn_downlink_not_ready:` from either step) a red banner explains why and
 * saving is disabled — for the empty-set rollback too (#3199).
 */
export function SamplingPoliciesSection(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { user } = useSession();
  const isAdmin = canAccessRoles(user?.roles ?? [], [ROLE_OPS_ADMIN]);
  const query = useSamplingPolicies();
  const putPolicies = usePutSamplingPolicies();
  const [draft, setDraft] = useState<PolicyDraftSet | null>(null);
  const [editing, setEditing] = useState<EditTarget | null>(null);
  const [warnings, setWarnings] = useState<readonly string[]>([]);
  const [conflict, setConflict] = useState(false);
  /** Warnings from the dry run, waiting for the operator's confirmation. */
  const [pendingWarnings, setPendingWarnings] = useState<readonly string[] | null>(null);
  /** A 403 `cn_downlink_not_ready:` from a save, shown until the refetched GET carries the gate. */
  const [gateError, setGateError] = useState<string | null>(null);

  const data = query.data;
  if (query.isPending) {
    return (
      <SectionFrame>
        <p className="m-0 text-body text-paper-low" data-testid="sampling-policies-loading">
          {t('tenant_routing.load.sampling.loading')}
        </p>
      </SectionFrame>
    );
  }
  if (data === undefined) {
    return (
      <SectionFrame>
        <p
          className="m-0 rounded bg-card px-3 py-2 text-dense text-paper-low"
          data-testid="sampling-policies-load-failed"
        >
          {t('tenant_routing.load.sampling.load_failed')}
        </p>
      </SectionFrame>
    );
  }

  const loaded = data;
  const rows = draft?.rows ?? loaded.policies.map(toPolicyInput);
  const saving = putPolicies.isPending;
  // An older gateway answers without `write_gate`: it has no gate, so open.
  const writeGate: SamplingPoliciesWriteGate | undefined = loaded.write_gate;
  const gateReason = gateError ?? (writeGate === undefined || writeGate.open ? null : (writeGate.reason ?? ''));
  // Every save is gated, the empty-set rollback included: since the lane
  // column (#3199) a DELETE stalls a CN that lacks it just like an INSERT.
  const gateClosed = gateReason !== null;

  function startDraft(): PolicyDraftSet {
    return draft ?? { rows: loaded.policies.map(toPolicyInput), baseVersion: loaded.version };
  }

  function replaceRows(next: readonly SamplingPolicyInput[]): void {
    setDraft({ ...startDraft(), rows: next });
    setWarnings([]);
  }

  function applyEdit(added: readonly SamplingPolicyInput[]): void {
    if (editing === null) return;
    const base = startDraft().rows;
    // One lane pick = one row; an edit swaps the edited row for the new ones.
    const next = editing.originalKey === null
      ? [...base, ...added]
      : base.flatMap((existing) => (samplingPolicyKey(existing) === editing.originalKey ? added : [existing]));
    replaceRows(next);
    setEditing(null);
  }

  function removeRow(key: string): void {
    replaceRows(startDraft().rows.filter((row) => samplingPolicyKey(row) !== key));
  }

  function discard(): void {
    setDraft(null);
    setConflict(false);
    setGateError(null);
  }

  function refresh(): void {
    discard();
    void query.refetch();
  }

  function saveFailed(error: Error): void {
    if (error instanceof OpsApiError && error.status === 409) {
      setConflict(true);
      return;
    }
    if (error instanceof OpsApiError && error.status === 403 && error.message.startsWith(CN_DOWNLINK_NOT_READY_PREFIX)) {
      setGateError(error.message);
      void query.refetch().then(() => setGateError(null));
      return;
    }
    toast.error(coerceErrorMessage(error));
  }

  function commit(): void {
    if (draft === null) return;
    setPendingWarnings(null);
    putPolicies.mutate(
      { policies: draft.rows, expected_version: draft.baseVersion },
      {
        onSuccess: (response) => {
          setDraft(null);
          setConflict(false);
          setGateError(null);
          setWarnings(response.warnings);
          toast.success(t('tenant_routing.load.sampling.toast.saved'));
        },
        onError: saveFailed,
      },
    );
  }

  function save(): void {
    if (draft === null || gateClosed) return;
    putPolicies.mutate(
      { policies: draft.rows, expected_version: draft.baseVersion, dry_run: true },
      {
        onSuccess: (response) => {
          if (response.warnings.length > 0) {
            setPendingWarnings(response.warnings);
            return;
          }
          commit();
        },
        onError: saveFailed,
      },
    );
  }

  const otherKeys = new Set(
    rows.map((row) => samplingPolicyKey(row)).filter((key) => key !== editing?.originalKey),
  );

  return (
    <SectionFrame>
      {gateClosed ? (
        <div
          role="alert"
          className="flex flex-col gap-1 rounded border border-bad-line bg-bad-dim/50 px-3 py-2"
          data-testid="sampling-policies-write-gate"
        >
          <p className="m-0 text-label text-bad-bright">{t('tenant_routing.load.sampling.write_gate.title')}</p>
          <p className="m-0 break-words font-mono text-dense leading-5 text-bad-bright">{gateReason}</p>
        </div>
      ) : null}

      {conflict ? (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-2 rounded border border-bad-line bg-bad-dim/50 px-3 py-2"
          data-testid="sampling-policies-conflict"
        >
          <p className="m-0 text-dense leading-5 text-bad-bright">{t('tenant_routing.load.sampling.conflict')}</p>
          <Button size="sm" onClick={refresh} data-testid="sampling-policies-refresh">
            {t('tenant_routing.load.sampling.refresh')}
          </Button>
        </div>
      ) : null}

      {warnings.length > 0 ? (
        <div
          className="flex flex-col gap-1 rounded border border-warn-line bg-warn-dim/50 px-3 py-2"
          data-testid="sampling-policies-warnings"
        >
          <p className="m-0 text-label text-warn">{t('tenant_routing.load.sampling.warnings_title')}</p>
          <ul className="m-0 list-disc pl-5 text-dense leading-5 text-warn">
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {SAMPLING_POLICY_GROUPS.map((group) => (
        <PolicyGroupCard
          key={`${group.stream}:${group.region}`}
          group={group}
          rows={policiesInGroup(rows, group)}
          source={loaded.sources[`${group.stream}:${group.region}`]}
          legacy={loaded.legacy_lifecycle}
          canEdit={isAdmin}
          saving={saving}
          onAdd={() => setEditing({ originalKey: null, initial: emptyDraft(group) })}
          onEdit={(row) => setEditing({ originalKey: samplingPolicyKey(row), initial: draftFromInput(row) })}
          onRemove={removeRow}
        />
      ))}

      {isAdmin && draft !== null ? (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded border border-signal/40 bg-signal-dim px-3 py-2"
          data-testid="sampling-policies-dirty"
        >
          <p className="m-0 text-dense text-paper">
            {t('tenant_routing.load.sampling.dirty', { count: String(draft.rows.length) })}
          </p>
          <span className="inline-flex items-center gap-2">
            <Button variant="ghost" size="sm" disabled={saving} onClick={discard} data-testid="sampling-policies-discard">
              {t('tenant_routing.load.sampling.discard')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={saving}
              disabled={gateClosed}
              onClick={save}
              data-testid="sampling-policies-save"
            >
              {t('tenant_routing.load.sampling.save')}
            </Button>
          </span>
        </div>
      ) : null}

      <ConfirmDialog
        open={pendingWarnings !== null}
        onOpenChange={(open) => {
          if (!open && !saving) setPendingWarnings(null);
        }}
        title={t('tenant_routing.load.sampling.confirm.title')}
        body={(
          <div className="flex flex-col gap-2">
            <p className="m-0">{t('tenant_routing.load.sampling.confirm.body')}</p>
            <ul className="m-0 list-disc pl-5 text-dense leading-5 text-warn" data-testid="sampling-policies-confirm-warnings">
              {(pendingWarnings ?? []).map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        )}
        confirmLabel={t('tenant_routing.load.sampling.confirm.save')}
        cancelLabel={t('tenant_routing.load.sampling.confirm.back')}
        closeLabel={t('common.close')}
        onConfirm={commit}
        loading={saving}
        testId="sampling-policies-confirm"
      />

      {editing !== null ? (
        <SamplingPolicyDialog
          isNew={editing.originalKey === null}
          initial={editing.initial}
          otherKeys={otherKeys}
          laneOptions={loaded.lane_options ?? []}
          onCancel={() => setEditing(null)}
          onApply={applyEdit}
        />
      ) : null}
    </SectionFrame>
  );
}

function SectionFrame({ children }: { readonly children: ReactNode }): JSX.Element {
  const t = useT();
  return (
    <section className="flex flex-col gap-5 rounded-lg border border-line bg-raised p-5" data-testid="sampling-policies-section">
      <div className="flex flex-col gap-3">
        <h3 className="m-0 text-section text-paper">{t('tenant_routing.load.sampling.title')}</h3>
        <p className="m-0 max-w-[72ch] text-dense leading-5 text-paper-low">{t('tenant_routing.load.sampling.hint')}</p>
        <div className="flex flex-col gap-2" data-testid="sampling-policies-ladder">
          <p className="m-0 text-label text-paper-mid">{t('tenant_routing.load.sampling.ladder')}</p>
          <ol className="m-0 flex list-none flex-wrap items-center gap-x-1.5 gap-y-2 p-0">
            {SAMPLING_SCOPES.map((scope, index) => (
              <li key={scope} className="inline-flex items-center gap-1.5">
                {index > 0 ? <span aria-hidden="true" className="text-paper-low">›</span> : null}
                <Chip tone="neutral" lamp={false}>
                  <span className="font-mono text-paper-low">{index + 1}</span>
                  <span className="text-paper-mid">{t(SCOPE_LABEL[scope])}</span>
                </Chip>
              </li>
            ))}
          </ol>
        </div>
      </div>
      {children}
    </section>
  );
}

function PolicyGroupCard({
  group,
  rows,
  source,
  legacy,
  canEdit,
  saving,
  onAdd,
  onEdit,
  onRemove,
}: {
  readonly group: SamplingPolicyGroup;
  readonly rows: readonly SamplingPolicyInput[];
  readonly source: SourceKind;
  readonly legacy: LegacyLifecycleSamplingValues | null;
  readonly canEdit: boolean;
  readonly saving: boolean;
  readonly onAdd: () => void;
  readonly onEdit: (row: SamplingPolicyInput) => void;
  readonly onRemove: (key: string) => void;
}): JSX.Element {
  const t = useT();
  const prefix = `sampling-policies-${group.stream}-${group.region}`;
  const inherit = <span className="font-sans text-paper-low">{t('tenant_routing.load.sampling.inherit')}</span>;
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-line bg-card p-4" data-testid={prefix}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
          <h4 className="m-0 text-section text-paper">
            {t(STREAM_LABEL[group.stream])} · {group.region.toUpperCase()}
          </h4>
          <Chip tone={SOURCE_TONE[source]} lamp={false} testId={`${prefix}-source`}>
            {t(SOURCE_LABEL[source])}
          </Chip>
          <span className="text-label text-paper-low" data-testid={`${prefix}-count`}>
            {t('tenant_routing.load.sampling.rows_count', { count: String(rows.length) })}
          </span>
        </span>
        {canEdit ? (
          <Button size="sm" disabled={saving} onClick={onAdd} data-testid={`${prefix}-add`}>
            {t('tenant_routing.load.sampling.add')}
          </Button>
        ) : null}
      </div>

      {group.stream === 'lifecycle' ? (
        <LegacyLifecycleLine region={group.region} legacy={legacy} superseded={source === 'table'} testId={`${prefix}-legacy`} />
      ) : null}

      {rows.length === 0 ? (
        <p className="m-0 text-dense text-paper-low" data-testid={`${prefix}-empty`}>
          {t(group.stream === 'trace'
            ? 'tenant_routing.load.sampling.empty.trace'
            : 'tenant_routing.load.sampling.empty.lifecycle')}
        </p>
      ) : (
        <Table testId={`${prefix}-table`} tableClassName="table-fixed">
          <colgroup>
            <col className="w-[124px]" />
            <col />
            <col className="w-[104px]" />
            {canEdit ? <col className="w-[132px]" /> : null}
          </colgroup>
          <THead>
            <Tr interactive={false}>
              <Th>{t('tenant_routing.load.sampling.col.layer')}</Th>
              <Th>{t('tenant_routing.load.sampling.col.target')}</Th>
              <Th num>{t('tenant_routing.load.sampling.field.keep_ratio')}</Th>
              {canEdit ? <Th>{t('tenant_routing.load.sampling.actions')}</Th> : null}
            </Tr>
          </THead>
          <TBody>
            {rows.map((row) => {
              const key = samplingPolicyKey(row);
              return (
                <Tr key={key} data-testid={`sampling-policy-row-${key}`}>
                  <Td className="align-top">
                    <Chip tone="neutral" lamp={false}>{t(SCOPE_LABEL[row.scope])}</Chip>
                  </Td>
                  <Td className="align-top">
                    <PolicyTarget row={row} rowKey={key} />
                    <PolicyDetails row={row} rowKey={key} />
                  </Td>
                  <Td num emphasis className="align-top text-body" data-testid={`sampling-policy-row-${key}-keep_ratio`}>
                    {row.keep_ratio === null ? inherit : String(row.keep_ratio)}
                  </Td>
                  {canEdit ? (
                    <Td className="align-top">
                      <span className="inline-flex items-center gap-1">
                        <Button size="sm" variant="ghost" disabled={saving} onClick={() => onEdit(row)} data-testid={`sampling-policy-row-${key}-edit`}>
                          {t('tenant_routing.load.sampling.edit')}
                        </Button>
                        <Button size="sm" variant="ghost" disabled={saving} onClick={() => onRemove(key)} data-testid={`sampling-policy-row-${key}-remove`}>
                          {t('tenant_routing.load.sampling.remove')}
                        </Button>
                      </span>
                    </Td>
                  ) : null}
                </Tr>
              );
            })}
          </TBody>
        </Table>
      )}
    </div>
  );
}

/** Who a row applies to: org (company over id), SKU, lane, or the whole region. */
function PolicyTarget({ row, rowKey }: { readonly row: SamplingPolicyInput; readonly rowKey: string }): JSX.Element {
  const t = useT();
  const lane = row.lane ?? null;
  if (row.org_id === null && row.sku_id === null && lane === null) {
    return <span className="text-dense text-paper-low">{t('tenant_routing.load.sampling.target.region')}</span>;
  }
  return (
    <div className="flex flex-col gap-1.5">
      {row.org_id === null ? null : (
        <CustomerCell
          orgId={row.org_id}
          secondary={<span className="self-start font-mono">{row.org_id}</span>}
          testId={`sampling-policy-customer-${rowKey}`}
        />
      )}
      {row.sku_id === null ? null : (
        <span className="font-mono text-dense text-paper" data-testid={`sampling-policy-sku-${rowKey}`}>
          {row.sku_id}
        </span>
      )}
      {lane === null ? null : (
        <span className="inline-flex items-center gap-1.5" data-testid={`sampling-policy-lane-${rowKey}`}>
          <span className="text-label text-paper-low">{t('tenant_routing.load.sampling.target.lane')}</span>
          <Chip tone="signal" lamp={false} className="font-mono">{lane}</Chip>
        </span>
      )}
    </div>
  );
}

/** The secondary values a row sets (omitted = inherited) and its note, kept off the main columns. */
function PolicyDetails({ row, rowKey }: { readonly row: SamplingPolicyInput; readonly rowKey: string }): JSX.Element | null {
  const t = useT();
  const parts: { readonly field: string; readonly text: string }[] = [];
  if (row.keep_slow_ms !== null) {
    parts.push({ field: 'keep_slow_ms', text: t('tenant_routing.load.sampling.detail.slow', { value: String(row.keep_slow_ms) }) });
  }
  if (row.size_threshold_bytes !== null) {
    parts.push({ field: 'size_threshold_bytes', text: t('tenant_routing.load.sampling.detail.size', { value: String(row.size_threshold_bytes) }) });
  }
  if (row.large_full_keep_ratio !== null) {
    parts.push({ field: 'large_full_keep_ratio', text: t('tenant_routing.load.sampling.detail.full', { value: String(row.large_full_keep_ratio) }) });
  }
  if (parts.length === 0 && row.note === null) return null;
  return (
    <div className="mt-2 flex flex-col gap-0.5 text-label text-paper-low">
      {parts.length === 0 ? null : (
        <p className="m-0 flex flex-wrap gap-x-3 gap-y-0.5">
          {parts.map((part) => (
            <span key={part.field} className="font-mono" data-testid={`sampling-policy-row-${rowKey}-${part.field}`}>
              {part.text}
            </span>
          ))}
        </p>
      )}
      {row.note === null ? null : (
        <p className="m-0 break-words" data-testid={`sampling-policy-row-${rowKey}-note`}>{row.note}</p>
      )}
    </div>
  );
}

function LegacyLifecycleLine({
  region,
  legacy,
  superseded,
  testId,
}: {
  readonly region: SamplingPolicyGroup['region'];
  readonly legacy: LegacyLifecycleSamplingValues | null;
  readonly superseded: boolean;
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  const unset = t('tenant_routing.load.sampling.legacy.unset');
  const p = legacy === null ? null : region === 'jp' ? legacy.lifecycle_sample_p_jp : legacy.lifecycle_sample_p_cn;
  const orgs = legacy?.lifecycle_keep_org_ids ?? null;
  const values = t('tenant_routing.load.sampling.legacy.values', {
    p: p === null ? unset : String(p),
    slow: legacy?.lifecycle_keep_slow_ms === null || legacy?.lifecycle_keep_slow_ms === undefined
      ? unset
      : String(legacy.lifecycle_keep_slow_ms),
    orgs: orgs === null || orgs.length === 0 ? unset : orgs.join(', '),
  });
  return (
    <p className="m-0 text-dense leading-5 text-paper-low" data-testid={testId}>
      {t(superseded ? 'tenant_routing.load.sampling.legacy.superseded' : 'tenant_routing.load.sampling.legacy.active')}
      {' '}
      <span className="font-mono">{values}</span>
    </p>
  );
}
